// Staff addresses read as the tree (user 2026-10-12): the subject, the group, the
// lesson, then what is in it — never a database id where a name will do.
//
//   /cabinet/teacher/<subject>                              the subject: its groups (om-102)
//   /cabinet/teacher/<subject>/<group>                      the group: its lessons (om-102/xy-3)
//   /cabinet/teacher/<subject>/<group>/journal              «Журнал групи»: its list and register (/students, the list until 2026-10-14, leads here)
//   /cabinet/teacher/<subject>/<group>/journal/<s>          one student of it: the first 8 characters of their id
//   /cabinet/teacher/<subject>/<group>/lesson/<n>           its lesson n (0 = the intro): «Матеріали» (Zoom, Miro, the recording)
//   /cabinet/teacher/<subject>/<group>/lesson/<n>/journal   the lesson's «Журнал заняття» (attendance)
//   /cabinet/teacher/<subject>/<group>/lesson/<n>/hw        the lesson's «ДЗ» (hw-2, hw-3: its second, third set)
//   /cabinet/teacher/<subject>/<group>/lesson/<n>/view      the lesson's «Що бачать учні» (/materials, its first tab until 2026-10-14, leads to lesson/<n>)
//   /cabinet/teacher/<subject>/<group>/lesson/<n>/hw/<w>    one student's work: the first 8 characters of its id
//
// <subject> is the group's current course's slug; <group> its name in Latin
// letters («XY-3» → xy-3). Whatever the book cannot name (a group with no course,
// a lesson without a number, homework set without a lesson) keeps its address
// by id: /cabinet/teacher/g/<id>, lesson/<id>, hw/<id>[/<id>] — those still open.
import * as api from './api.js';

const LAT = {
    а: 'a', б: 'b', в: 'v', г: 'h', ґ: 'g', д: 'd', е: 'e', є: 'ie', ж: 'zh', з: 'z', и: 'y', і: 'i', ї: 'i', й: 'i',
    к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'kh', ц: 'ts', ч: 'ch',
    ш: 'sh', щ: 'shch', ь: '', ю: 'iu', я: 'ia', ы: 'y', э: 'e', ё: 'io', ъ: '',
};
export const latin = (s) => String(s || '').toLowerCase().replace(/[’'ʼ`]/g, '')
    .replace(/[а-яіїєґёыэъ]/g, c => LAT[c] ?? '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
export const workKey = (submissionId) => String(submissionId).slice(0, 8);

let B = null, bookP = null, bookAt = null;
// Loaded once per visit; a page awaits it beside its own data. A path the book
// does not know reloads it once (a group or homework made since). `fresh` (the
// teacher's home) reloads it only after a write or a minute, as api.kept does.
export function addressBook(fresh = false) {
    if (fresh && (bookAt?.v !== api.dataVersion() || Date.now() - bookAt.t > 60e3)) bookP = null;
    if (!bookP) bookAt = { v: api.dataVersion(), t: Date.now() };
    return bookP ||= load().then(b => (B = b), (e) => { bookP = null; throw e; });
}
async function load() {
    const groups = await api.myGroups();
    const gids = groups.map(g => g.id);
    const [gcs, courses, lessons, hws] = gids.length ? await Promise.all([
        api.q(api.db().from('group_courses').select('group_id,course_id').in('group_id', gids).is('ends_on', null)),
        api.q(api.db().from('courses').select('id,slug,name')),
        api.q(api.db().from('lessons').select('id,group_id,index_no,starts_at').in('group_id', gids).order('starts_at', { nullsFirst: true })),
        api.q(api.db().from('assignments').select('id,group_id,lesson_id,created_at').in('group_id', gids).order('created_at')),
    ]) : [[], [], [], []];
    const slug = Object.fromEntries(courses.map(c => [c.id, c.slug]));
    const subj = Object.fromEntries(gcs.map(x => [x.group_id, slug[x.course_id]]));
    const group = {}, lesson = {}, hw = {}, byPath = {}, subject = {}, groupSubject = {};
    // A subject is the course its groups follow now; its address is the course's slug.
    for (const c of courses) {
        const gs = groups.filter(g => subj[g.id] === c.slug);
        if (!gs.length || RESERVED.includes(c.slug)) continue;
        const p = `/cabinet/teacher/${c.slug}`;
        subject[c.slug] = { slug: c.slug, id: c.id, name: c.name, href: p, groups: gs.map(g => g.id) };
        byPath[p] = { subject: c.slug };
    }
    for (const g of [...groups].sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)))) {
        const s = subj[g.id], n = latin(g.name);
        if (!s || !n || !subject[s]) continue;
        let p = `/cabinet/teacher/${s}/${n}`;
        for (let k = 2; byPath[p]; k++) p = `/cabinet/teacher/${s}/${n}-${k}`;
        group[g.id] = p; groupSubject[g.id] = s; byPath[p] = { group: g.id };
    }
    const lessonOf = {};
    for (const l of lessons) {
        const p = group[l.group_id] && l.index_no != null ? `${group[l.group_id]}/lesson/${l.index_no}` : null;
        if (!p || byPath[p]) continue;   // two lessons with one number: the first keeps it
        lesson[l.id] = p; byPath[p] = { lesson: l.id };
        byPath[`${p}/materials`] = { lesson: l.id, tab: 'materials' };
        byPath[`${p}/journal`] = { lesson: l.id, tab: 'journal' };
        byPath[`${p}/hw`] = { lesson: l.id, tab: 'hw' };
        byPath[`${p}/view`] = { lesson: l.id, tab: 'view' };
        lessonOf[p] = l.id;
    }
    const count = {};
    for (const a of hws) {
        const L = lesson[a.lesson_id];
        if (!L) continue;
        const k = count[L] = (count[L] || 0) + 1;
        const p = `${L}/${k === 1 ? 'hw' : 'hw-' + k}`;
        hw[a.id] = p; byPath[p] = { lesson: a.lesson_id, tab: 'hw', hw: a.id };
    }
    return { group, lesson, hw, byPath, subject, groupSubject };
}
// The teacher's own pages, never a subject's slug.
const RESERVED = ['review', 'program', 'help', 'bank', 'g', 'lesson', 'hw'];

// Sync once the book is in (pages await addressBook() first); before that, by id.
export const groupHref = (id) => B?.group[id] || `/cabinet/teacher/g/${id}`;
export const lessonHref = (id) => B?.lesson[id] || `/cabinet/teacher/lesson/${id}`;
export const hwHref = (id) => B?.hw[id] || `/cabinet/teacher/hw/${id}`;
export const workHref = (aid, submissionId) => B?.hw[aid] ? `${B.hw[aid]}/${workKey(submissionId)}` : `/cabinet/teacher/hw/${aid}/${submissionId}`;
// A lesson's tab: '' «Матеріали», 'journal' «Журнал заняття», 'hw' «ДЗ», 'view' «Що бачать учні».
export const lessonTabHref = (id, tab = '') => B?.lesson[id] ? B.lesson[id] + (tab ? '/' + tab : '') : `/cabinet/teacher/lesson/${id}${tab ? '#' + tab : ''}`;
export const journalHref = (gid) => `${groupHref(gid)}/journal`;
export const pupilHref = (gid, sid) => `${journalHref(gid)}/${String(sid).slice(0, 8)}`;
// The subjects in the book, and the one a group follows ({ slug, name, href, groups }).
export const subjects = () => Object.values(B?.subject || {});
export const subjectOfGroup = (gid) => B?.subject[B?.groupSubject[gid]] || null;

// /cabinet/teacher/<subject>[/<group>[/journal[/<s>] | /students | /lesson/<n>[/journal | /hw[-k][/<w>] | /view | /materials]]]
//   → { subject } | { group, tab?, pupil? } | { lesson, tab?, hw? } | { lesson, hw, work } | null.
export async function resolve(path) {
    const m = path.match(/^(\/cabinet\/teacher\/[a-z0-9-]+(?:\/[a-z0-9-]+(?:\/lesson\/\d+(?:\/(?:hw(?:-\d+)?|journal|materials|view))?)?)?)(?:\/([0-9a-f]{8}|students|journal(?:\/[0-9a-f]{8})?))?$/);
    if (!m) return null;
    let hit = (await addressBook()).byPath[m[1]];
    if (!hit) hit = (await addressBook(true)).byPath[m[1]];
    if (!hit) return null;
    if (!m[2]) return hit;
    if (m[2] === 'students' || m[2].startsWith('journal')) return hit.group ? { group: hit.group, tab: m[2] === 'students' ? 'students' : 'journal', pupil: m[2].split('/')[1] || null } : null;
    if (!hit.hw) return null;
    const subs = await api.q(api.db().from('submissions').select('id').eq('assignment_id', hit.hw));
    const s = subs.find(x => workKey(x.id) === m[2]);
    return s ? { lesson: hit.lesson, hw: hit.hw, work: s.id } : null;
}
